import React, { useContext } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  Image,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAcceptInvitationStyles } from '../styles';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

export type ActivationDeniedDialogProps = {
  open: boolean;
  // The blocked sign-in identity (username/email), shown inline so the user knows which account was
  // rejected. Optional — the copy falls back to a generic phrase when unavailable.
  email?: string | null;
  // "Use a different email" — sign the current session out and return to the invitation choice view
  // so the user can retry with a different account. Resolved by the caller.
  onUseDifferentEmail: () => void;
};

// Shown when ActivationOptions reports `ActivationDenied` — the signed-in identity exists but cannot
// accept this invitation (the legacy flow's PromptToChangeIdentity "identity blocked" outcome, which
// covers the `*CannotSignIn` / `*CannotBeMerged` ResultCodes). The only path forward is to sign in
// with a different email (or contact support to unblock the user name), so we surface a single
// action. Built as a dialog, mirroring ExistingProfileDialog.
export const ActivationDeniedDialog: React.FC<ActivationDeniedDialogProps> = ({
  open,
  email,
  onUseDifferentEmail,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  const title = i18n.getString(_TL_('This account can\'t be used'));
  const body = email
    ? i18n.getString(
      _TL_('Unfortunately, {{email}} is not allowed to sign in to Microsoft Advertising. Please sign in with a different email address or contact support to unblock this user name.'),
      { email }
    )
    : i18n.getString(_TL_('Unfortunately, this account is not allowed to sign in to Microsoft Advertising. Please sign in with a different email address or contact support to unblock this user name.'));

  return (
    <Dialog open={open} modalType="alert">
      <DialogSurface
        className={classes.dialogSurface}
        aria-labelledby="activation-denied-dialog-title"
        aria-describedby="activation-denied-dialog-description"
      >
        <DialogBody className="activation-denied-dialog">
          <DialogContent className={classes.dialogContent}>
            <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />
            <Title3 id="activation-denied-dialog-title" className={classes.dialogTitle}>{title}</Title3>
            <Text id="activation-denied-dialog-description" block>{body}</Text>
            <div className={classes.dialogActions}>
              <Button
                className="activation-denied-use-different-email"
                appearance="primary"
                onClick={onUseDifferentEmail}
              >
                {i18n.getString(_TL_('Use a different email'))}
              </Button>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
